import type { ServiceArea } from "@/config/service-areas";
import type { ServicePage } from "@/config/services";
import type { LocaleCode } from "@/i18n/routing";
import zhHans from "./zh-hans";
import zhHant from "./zh-hant";
import pa from "./pa";
import ko from "./ko";
import fa from "./fa";
import ar from "./ar";
import { localizeAreaName } from "../area-names";

type DeepWiden<T> = T extends string
  ? string
  : T extends readonly (infer Item)[]
    ? readonly DeepWiden<Item>[]
    : T extends object
      ? { readonly [Key in keyof T]: DeepWiden<T[Key]> }
      : T;

export type PageUi = DeepWiden<typeof zhHans.ui> & {
  readonly areaIndex?: {
    readonly breadcrumbLabel: string;
    readonly eyebrow: string;
    readonly title: string;
    readonly body: string;
    readonly coverageEyebrow: string;
    readonly coverageTitle: string;
    readonly coverageBody: string;
    readonly nearbyEyebrow: string;
    readonly nearbyTitle: string;
    readonly contactCta: string;
  };
};

type ContentBundle = {
  /** Partial overrides; new ServicePage fields fall back to English catalogue copy. */
  services: Record<string, Partial<Omit<ServicePage, "slug" | "image">>>;
  serviceAreas: Record<
    string,
    Pick<ServiceArea, "intro" | "localContext" | "assessmentFocus" | "propertyContexts" | "faqs">
  >;
  ui?: PageUi;
};

/** Maps published service slugs to legacy content bundle keys. */
const SERVICE_CONTENT_ALIASES: Record<string, string> = {
  "emergency-roof-leak-repair": "roof-repair",
  "asphalt-shingle-replacement": "roof-replacement",
  "metal-roof-replacement": "metal-roofing",
  "flat-roof-replacement-sbs-tpo-pvc": "flat-roofing",
  "roof-health-check-maintenance": "roof-inspection-maintenance",
  "moss-removal-roof-gutter": "roof-inspection-maintenance",
};

const localizedContent: Partial<Record<LocaleCode, ContentBundle>> = {
  "zh-cn": zhHans,
  "zh-tw": zhHant,
  "pa-in": {
    services: pa.services,
    serviceAreas: pa.serviceAreas,
    ui: adaptAlternateUi(pa.ui, pa.services, {
      privacy: [
        [
          "ਤੁਹਾਡੇ ਵੱਲੋਂ ਦਿੱਤੀ ਜਾਣਕਾਰੀ",
          "ਪੁੱਛਗਿੱਛ ਫਾਰਮ ਤੁਹਾਡਾ ਨਾਮ, ਫ਼ੋਨ, ਈਮੇਲ, ਪੋਸਟਲ ਕੋਡ, ਸੇਵਾ ਦੀ ਲੋੜ, ਸਮਾਂ-ਸੀਮਾ ਅਤੇ ਸੁਨੇਹਾ ਇਕੱਠਾ ਕਰ ਸਕਦਾ ਹੈ। ਸਬਮਿਸ਼ਨ ਕੇਵਲ ਮਨਜ਼ੂਰਸ਼ੁਦਾ ਲੀਡ ਸੇਵਾ ਕਨਫ਼ਿਗਰ ਹੋਣ ਤੇ ਭੇਜੀ ਜਾਂਦੀ ਹੈ।",
        ],
        [
          "ਐਟ੍ਰਿਬਿਊਸ਼ਨ ਅਤੇ ਤਕਨੀਕੀ ਜਾਣਕਾਰੀ",
          "ਮੁਹਿੰਮ ਦਾ ਸਰੋਤ ਸਮਝਣ ਲਈ UTM ਮੁੱਲ ਅਤੇ ਵਿਗਿਆਪਨ ਕਲਿੱਕ ਪਛਾਣਕਰਤਾ ਸੈਸ਼ਨ ਸਟੋਰੇਜ ਵਿੱਚ ਰੱਖੇ ਜਾ ਸਕਦੇ ਹਨ। ਵਿਸ਼ਲੇਸ਼ਣ ਇਵੈਂਟਾਂ ਵਿੱਚ ਫਾਰਮ ਦੇ ਨਿੱਜੀ ਖੇਤਰ ਨਹੀਂ ਭੇਜੇ ਜਾਂਦੇ।",
        ],
        [
          "ਜਾਣਕਾਰੀ ਦੀ ਵਰਤੋਂ",
          "ਪੁੱਛਗਿੱਛ ਦੀ ਜਾਣਕਾਰੀ ਤੁਹਾਡੀ ਛੱਤ ਸੰਬੰਧੀ ਬੇਨਤੀ ਦਾ ਜਵਾਬ ਦੇਣ, ਪ੍ਰੋਜੈਕਟ ਦੀ ਲੋੜ ਸਮਝਣ ਅਤੇ ਸੰਬੰਧਤ ਕਾਰੋਬਾਰੀ ਰਿਕਾਰਡ ਰੱਖਣ ਲਈ ਵਰਤੀ ਜਾਂਦੀ ਹੈ।",
        ],
        [
          "ਸੇਵਾ ਪ੍ਰਦਾਤਾ ਅਤੇ ਰੱਖ-ਰਖਾਵ",
          "ਮਨਜ਼ੂਰਸ਼ੁਦਾ ਹੋਸਟਿੰਗ, ਵਿਸ਼ਲੇਸ਼ਣ ਅਤੇ ਲੀਡ-ਡਿਲਿਵਰੀ ਪ੍ਰਦਾਤਾ Revolin ਦੀ ਓਰੋਂ ਜਾਣਕਾਰੀ ਸੰਭਾਲ ਸਕਦੇ ਹਨ। ਪਹੁੰਚ ਵਾਜਬ ਕਾਰੋਬਾਰੀ, ਕਾਨੂੰਨੀ ਅਤੇ ਸੁਰੱਖਿਆ ਲੋੜਾਂ ਤੱਕ ਸੀਮਿਤ ਰਹਿੰਦੀ ਹੈ।",
        ],
        [
          "ਤੁਹਾਡੀਆਂ ਚੋਣਾਂ",
          "ਆਪਣੀ ਪੁੱਛਗਿੱਛ ਨਾਲ ਸੰਬੰਧਤ ਨਿੱਜੀ ਜਾਣਕਾਰੀ ਬਾਰੇ ਪੁੱਛਣ ਲਈ Revolin Group ਨਾਲ ਸੰਪਰਕ ਕਰੋ। ਬ੍ਰਾਊਜ਼ਰ ਸਟੋਰੇਜ ਬ੍ਰਾਊਜ਼ਰ ਸੈਟਿੰਗਾਂ ਰਾਹੀਂ ਮਿਟਾਈ ਜਾ ਸਕਦੀ ਹੈ।",
        ],
      ],
      terms: [
        [
          "ਆਮ ਜਾਣਕਾਰੀ",
          "ਵੈੱਬਸਾਈਟ ਸਮੱਗਰੀ ਆਮ ਸਿੱਖਿਆ ਅਤੇ ਯੋਜਨਾਬੰਦੀ ਲਈ ਹੈ। ਇਹ ਇੰਜੀਨੀਅਰਿੰਗ ਰਾਏ, ਬਿਲਡਿੰਗ ਕੋਡ ਨਿਰਣੇ ਜਾਂ ਕਿਸੇ ਖਾਸ ਪ੍ਰੋਜੈਕਟ ਦੀ ਸਿਫਾਰਸ਼ ਨਹੀਂ ਹੈ।",
        ],
        [
          "ਪ੍ਰੋਜੈਕਟ ਦਾ ਦਾਇਰਾ",
          "ਕੀਮਤ, ਸਮਾਂ-ਸੂਚੀ, ਉਤਪਾਦ, ਵਾਰੰਟੀ ਅਤੇ ਬਾਹਰ ਰੱਖੀਆਂ ਚੀਜ਼ਾਂ ਕੇਵਲ ਦਸਤਖ਼ਤ ਕੀਤੇ ਪ੍ਰੋਜੈਕਟ ਦਸਤਾਵੇਜ਼ ਵਿੱਚ ਦਰਜ ਹੋਣ ਤੇ ਲਾਗੂ ਹੁੰਦੀਆਂ ਹਨ।",
        ],
        [
          "ਤਸਵੀਰਾਂ ਅਤੇ ਪ੍ਰੋਜੈਕਟ ਸਬੂਤ",
          "ਵੈੱਬਸਾਈਟ ਤਸਵੀਰਾਂ ਅਤੇ ਪ੍ਰੋਜੈਕਟ ਸਮੱਗਰੀ ਆਮ ਯੋਜਨਾਬੰਦੀ ਲਈ ਹਨ। ਕਾਰਜ-ਖੇਤਰ, ਵਾਰੰਟੀ ਅਤੇ ਵਚਨਬੱਧਤਾ ਕੇਵਲ ਦਸਤਖ਼ਤ ਕੀਤੇ ਪ੍ਰੋਜੈਕਟ ਦਸਤਾਵੇਜ਼ ਵਿੱਚ ਦਰਜ ਹੋਣ ਤੇ ਲਾਗੂ ਹੁੰਦੀਆਂ ਹਨ।",
        ],
        [
          "ਬਾਹਰੀ ਲਿੰਕ",
          "ਬਾਹਰੀ ਵੈੱਬਸਾਈਟਾਂ ਦੇ ਲਿੰਕ ਸੰਦਰਭ ਲਈ ਹਨ। ਉਨ੍ਹਾਂ ਦੀ ਸਮੱਗਰੀ ਅਤੇ ਗੋਪਨੀਯਤਾ ਅਭਿਆਸ ਉਨ੍ਹਾਂ ਦੇ ਆਪਰੇਟਰਾਂ ਦੇ ਅਧੀਨ ਹਨ।",
        ],
        [
          "ਸਵੀਕਾਰਯੋਗ ਵਰਤੋਂ",
          "ਸਾਈਟ ਦੀ ਦੁਰਵਰਤੋਂ, ਬਿਨਾਂ ਅਧਿਕਾਰ ਪਹੁੰਚ ਦੀ ਕੋਸ਼ਿਸ਼ ਜਾਂ ਫਾਰਮ ਰਾਹੀਂ ਗੈਰਕਾਨੂੰਨੀ ਜਾਂ ਭ੍ਰਮਕ ਸਮੱਗਰੀ ਨਾ ਭੇਜੋ।",
        ],
      ],
    }),
  },
  "ko-kr": {
    services: ko.services,
    serviceAreas: ko.serviceAreas,
    ui: adaptAlternateUi(ko.ui, ko.services, {
      privacy: [
        [
          "제공하는 정보",
          "문의 양식은 이름, 전화번호, 이메일, 우편번호, 필요한 서비스, 희망 일정과 메시지를 수집할 수 있습니다. 승인된 문의 전달 서비스가 설정된 경우에만 제출됩니다.",
        ],
        [
          "유입 및 기술 정보",
          "문의 유입 경로를 파악하기 위해 UTM 값과 광고 클릭 식별자가 세션 저장소에 보관될 수 있습니다. 분석 이벤트에는 양식의 개인 정보 필드를 포함하지 않습니다.",
        ],
        [
          "정보 이용 목적",
          "문의 정보는 지붕 관련 요청에 답변하고 프로젝트 필요를 이해하며 관련 업무 기록을 유지하는 데 사용됩니다.",
        ],
        [
          "서비스 제공업체 및 보관",
          "승인된 호스팅, 분석 및 문의 전달 제공업체가 Revolin을 대신해 정보를 처리할 수 있습니다. 접근은 정당한 업무, 법률 및 보안 필요로 제한됩니다.",
        ],
        [
          "선택권",
          "문의와 관련된 개인정보에 대해 Revolin Group에 문의할 수 있습니다. 브라우저 저장 정보는 브라우저 설정에서 삭제할 수 있습니다.",
        ],
      ],
      terms: [
        [
          "일반 정보",
          "웹사이트 콘텐츠는 일반적인 교육과 계획을 위한 것이며 엔지니어링 의견, 건축법 판단, 검사 인증서 또는 특정 프로젝트 권고가 아닙니다.",
        ],
        [
          "프로젝트 범위",
          "가격, 일정, 제품 선택, 보증 및 제외 사항은 서명된 프로젝트 문서에 명시된 경우에만 적용됩니다.",
        ],
        [
          "이미지와 프로젝트 자료",
          "웹사이트 이미지와 프로젝트 자료는 일반적인 계획 참고용입니다. 작업 범위, 보증 및 약정은 서명된 프로젝트 문서에 명시된 경우에만 적용됩니다.",
        ],
        [
          "외부 링크",
          "외부 웹사이트 링크는 참고용입니다. 해당 사이트의 콘텐츠와 개인정보 처리 방식은 각 운영자가 관리합니다.",
        ],
        [
          "허용되는 이용",
          "사이트를 오용하거나 무단 접근을 시도하거나 양식을 통해 불법·기만적 자료를 제출해서는 안 됩니다.",
        ],
      ],
    }),
  },
  "fa-ir": {
    services: fa.services,
    serviceAreas: fa.serviceAreas,
    ui: adaptAlternateUi(fa.ui, fa.services, {
      privacy: [
        [
          "اطلاعاتی که ارائه می‌دهید",
          "فرم درخواست ممکن است نام، شماره تلفن، ایمیل، کد پستی، خدمت موردنیاز، زمان‌بندی دلخواه و پیام شما را دریافت کند. ثبت نهایی زمانی تأیید می‌شود که سامانهٔ ارسال درخواست فعال باشد و اطلاعات را بپذیرد.",
        ],
        [
          "منبع و اطلاعات فنی",
          "ممکن است مقادیر UTM و شناسه‌های کلیک تبلیغ در حافظهٔ موقت مرورگر نگهداری شوند تا منبع درخواست مشخص شود. اطلاعات شخصی واردشده در فرم همراه رویدادهای آماری ارسال نمی‌شود.",
        ],
        [
          "استفاده از اطلاعات",
          "اطلاعات درخواست برای پاسخ به درخواست سقف، درک نیاز پروژه و نگهداری سوابق کسب‌وکار مرتبط استفاده می‌شود.",
        ],
        [
          "ارائه‌دهندگان خدمات و نگهداری",
          "ارائه‌دهندگان تأییدشدهٔ میزبانی، آمار و ارسال درخواست ممکن است از طرف رولین اطلاعات را پردازش کنند. دسترسی فقط به اندازهٔ نیازهای کاری، قانونی و امنیتی مجاز است.",
        ],
        [
          "انتخاب‌های شما",
          "برای پرسش درباره اطلاعات شخصی مرتبط با درخواست خود با Revolin Group تماس بگیرید. ذخیره‌سازی مرورگر را می‌توان از تنظیمات مرورگر پاک کرد.",
        ],
      ],
      terms: [
        [
          "اطلاعات عمومی",
          "محتوای وب‌سایت برای آموزش و برنامه‌ریزی عمومی است. نظر مهندسی، تصمیم کد ساختمان یا توصیه پروژه خاص نیست.",
        ],
        [
          "شرح کار پروژه",
          "قیمت، زمان‌بندی، محصول، گارانتی و موارد مستثنی فقط وقتی اعمال می‌شوند که در سند پروژه امضاشده ذکر شده باشند.",
        ],
        [
          "تصاویر و شواهد پروژه",
          "تصاویر و مطالب پروژه در وب‌سایت فقط برای آشنایی و برنامه‌ریزی عمومی ارائه می‌شوند. شرح کار، ضمانت و تعهدات زمانی معتبرند که در اسناد امضاشدهٔ پروژه درج شده باشند.",
        ],
        [
          "پیوندهای خارجی",
          "پیوند به وب‌سایت‌های خارجی برای مرجع است. محتوا و شیوه حریم خصوصی آن‌ها تحت کنترل اپراتورهایشان است.",
        ],
        [
          "استفاده قابل‌قبول",
          "از سوءاستفاده از سایت، تلاش برای دسترسی بدون مجوز یا ارسال مطالب غیرقانونی یا فریبنده از طریق فرم خودداری کنید.",
        ],
      ],
    }),
  },
  "ar-sa": {
    services: ar.services,
    serviceAreas: ar.serviceAreas,
    ui: adaptAlternateUi(ar.ui, ar.services, {
      privacy: [
        [
          "المعلومات التي تقدمها",
          "قد يجمع نموذج الطلب الاسم ورقم الهاتف والبريد الإلكتروني والرمز البريدي والخدمة المطلوبة والموعد المفضل والرسالة. لا يُؤكَّد الإرسال إلا عندما يكون نظام استقبال الطلبات مفعّلاً ويقبل البيانات.",
        ],
        [
          "المصدر والمعلومات التقنية",
          "قد تُحفظ قيم UTM ومعرّفات النقر على الإعلانات في ذاكرة الجلسة لمعرفة مصدر الطلب. ولا تتضمن أحداث التحليل البيانات الشخصية المدخلة في النموذج.",
        ],
        [
          "استخدام المعلومات",
          "تُستخدم معلومات الاستفسار للرد على طلب السقف وفهم احتياج المشروع والاحتفاظ بسجلات العمل ذات الصلة.",
        ],
        [
          "مقدمو الخدمة والاحتفاظ",
          "قد يعالج مقدمو خدمات الاستضافة والتحليلات وإرسال الطلبات المعتمدون المعلومات نيابة عن رولين. ويقتصر الوصول على ما تقتضيه الاحتياجات التشغيلية والقانونية والأمنية.",
        ],
        [
          "خياراتك",
          "تواصل مع Revolin Group للسؤال عن المعلومات الشخصية المرتبطة باستفسارك. يمكن مسح تخزين المتصفح من إعدادات المتصفح.",
        ],
      ],
      terms: [
        [
          "معلومات عامة",
          "محتوى الموقع للتعليم والتخطيط العام. ليس رأياً هندسياً أو قرار كود بناء أو توصية مشروع محددة.",
        ],
        [
          "نطاق المشروع",
          "السعر والجدول الزمني والمنتج والضمان والاستثناءات تنطبق فقط عندما تُذكر في وثيقة المشروع الموقعة.",
        ],
        [
          "الصور وأدلة المشروع",
          "صور الموقع ومواد المشروع للمرجعية التخطيطية العامة. ينطبق النطاق والضمان والالتزام فقط عندما تُذكر في وثيقة المشروع الموقعة.",
        ],
        ["روابط خارجية", "روابط المواقع الخارجية للمرجع. محتواها وممارسات الخصوصية تحت مشغليها."],
        [
          "الاستخدام المقبول",
          "لا تسيء استخدام الموقع أو تحاول الوصول دون إذن أو ترسل مواد غير قانونية أو مضللة عبر النموذج.",
        ],
      ],
    }),
  },
};

type AlternateUi = typeof pa.ui | typeof ko.ui | typeof fa.ui | typeof ar.ui;
type AlternateServices =
  typeof pa.services | typeof ko.services | typeof fa.services | typeof ar.services;
type LegalPairs = {
  privacy: readonly (readonly [string, string])[];
  terms: readonly (readonly [string, string])[];
};

function adaptAlternateUi(
  raw: AlternateUi,
  services: AlternateServices,
  legal: LegalPairs,
): PageUi {
  const serviceTitles = services as unknown as Record<string, { title: string }>;
  return {
    common: {
      home: raw.common.home,
      services: raw.common.services,
      serviceAreas: raw.common.serviceAreas,
      requestAssessment: raw.common.requestAssessment,
      exploreServices: raw.common.exploreServices,
      learnMore: raw.common.learnMore,
      nextStep: raw.serviceDetail.nextStepEyebrow,
      questions: raw.serviceDetail.questionsEyebrow,
      call: raw.serviceDetail.call,
    },
    serviceDetail: {
      breadcrumbLabel: raw.serviceDetail.breadcrumbLabel,
      whoFor: raw.serviceDetail.whoForEyebrow,
      conditionFirstTitle: raw.serviceDetail.whoForTitle,
      warningSigns: raw.serviceDetail.warningSignsTitle,
      assessmentDiscipline: raw.serviceDetail.assessmentEyebrow,
      evaluatesTitle: raw.serviceDetail.evaluatesTitle,
      solutionCategories: raw.serviceDetail.solutionsEyebrow,
      solutionsTitle: raw.serviceDetail.solutionsTitle,
      repairMakesSense: raw.serviceDetail.repairEyebrow,
      replacementMakesSense: raw.serviceDetail.replaceEyebrow,
      scope: raw.serviceDetail.scopeEyebrow,
      scopeTitle: raw.serviceDetail.scopeTitle,
      scopeBody: raw.serviceDetail.scopeBody,
      beforeDecide: raw.serviceDetail.questionsTitle,
      assessmentTitle: raw.serviceDetail.nextStepTitle,
      assessmentBody: raw.serviceDetail.nextStepBody,
    },
    serviceArea: {
      regions: {
        metroVancouver: raw.serviceArea.metroVancouver,
        lowerMainland: raw.serviceArea.lowerMainland,
      },
      roofingServicesSuffix: raw.serviceArea.roofingServicesSuffix,
      titlePrefix: raw.serviceArea.titlePrefix.includes("{area}")
        ? raw.serviceArea.titlePrefix
        : `{area} ${raw.serviceArea.titlePrefix}`,
      confirmedCommunity: raw.serviceArea.confirmedCommunity,
      localContext: raw.serviceArea.localContextEyebrow,
      propertyDecides: raw.serviceArea.localContextTitle,
      propertyContexts: raw.serviceArea.propertyContextsLabel,
      assessmentPriorities: raw.serviceArea.prioritiesEyebrow,
      attentionPrefix: raw.serviceArea.prioritiesTitle,
      planningDisclaimer: raw.serviceArea.planningDisclaimer,
      relatedServices: raw.serviceArea.relatedEyebrow,
      decisionsPrefix: raw.serviceArea.relatedTitle,
      relatedBody: raw.serviceArea.relatedBody,
      genericService: raw.serviceArea.fallbackServiceName,
      faqSuffix: raw.serviceArea.faqSuffix,
      faqTitle: raw.serviceArea.faqTitle,
      requestAssessment: raw.serviceArea.requestEyebrow,
      assessmentTitle: raw.serviceArea.requestTitle,
      assessmentBodyPrefix: raw.serviceArea.requestBody,
      contactOptions: raw.common.viewContactOptions,
      serviceNames: {
        roofRepair: serviceTitles["roof-repair"]?.title ?? "Roof repair",
        roofReplacement: serviceTitles["roof-replacement"]?.title ?? "Roof replacement",
        asphaltShingles: serviceTitles["asphalt-shingle-roofing"]?.title ?? "Asphalt shingles",
        metalRoofing: serviceTitles["metal-roofing"]?.title ?? "Metal roofing",
        flatRoofing: serviceTitles["flat-roofing"]?.title ?? "Flat roofing",
        inspectionMaintenance:
          serviceTitles["roof-inspection-maintenance"]?.title ?? "Roof inspection",
      },
    },
    servicesIndex: {
      eyebrow: raw.servicesIndex.eyebrow,
      title: raw.servicesIndex.title,
      body: raw.servicesIndex.body,
      exploreService: raw.servicesIndex.exploreService,
      technicalContext: raw.servicesIndex.technicalEyebrow,
      sourcesTitle: raw.servicesIndex.technicalTitle,
      roofingManual: raw.servicesIndex.rcabcTitle,
      roofingManualBody: raw.servicesIndex.rcabcBody,
      bcCodes: raw.servicesIndex.bcCodesTitle,
      bcCodesBody: raw.servicesIndex.bcCodesBody,
      vancouverBylaw: raw.servicesIndex.vancouverBylawTitle,
      vancouverBylawBody: raw.servicesIndex.vancouverBylawBody,
    },
    areaIndex: raw.areaIndex,
    corePages: {
      about: {
        eyebrow: raw.about.eyebrow,
        title: raw.about.title,
        body: raw.about.body,
        relationship: raw.about.relationshipEyebrow,
        relationshipTitle: raw.about.relationshipTitle,
        relationshipBody: [raw.about.relationshipBody1, raw.about.relationshipBody2],
        principles: raw.about.principlesEyebrow,
        principlesTitle: raw.about.principlesTitle,
        principleItems: [
          { title: raw.about.assessTitle, body: raw.about.assessBody },
          { title: raw.about.architectureTitle, body: raw.about.architectureBody },
          { title: raw.about.limitsTitle, body: raw.about.limitsBody },
        ],
        parentCompany: raw.about.parentEyebrow,
        parentTitle: raw.about.parentTitle,
        parentBody: raw.about.parentBody,
        parentLink: raw.about.parentCta,
      },
      gallery: raw.beforeAfter,
      faq: raw.faqPage,
      contact: {
        eyebrow: raw.contactPage.eyebrow,
        title: raw.contactPage.title,
        body: raw.contactPage.body,
        direct: raw.contactPage.directEyebrow,
        basics: raw.contactPage.directTitle,
        phone: raw.contactPage.phone,
        email: raw.contactPage.email,
        serviceRegion: raw.contactPage.serviceRegion,
        regionValue: raw.contactPage.regionValue,
      },
      legal: {
        legal: raw.legal.legal,
        privacyTitle: raw.legal.privacyPolicy,
        termsTitle: raw.legal.websiteTerms,
        privacyBody: raw.legal.privacyBody,
        termsBody: raw.legal.termsBody,
        lastUpdated: raw.legal.lastUpdated,
        privacySections: legal.privacy.map(([title, body]) => ({ title, body })),
        termsSections: legal.terms.map(([title, body]) => ({ title, body })),
      },
      assessment: {
        title: raw.serviceDetail.nextStepTitle,
        body: raw.serviceDetail.nextStepBody,
      },
    },
  };
}

export function getPageUi(locale: LocaleCode): PageUi | undefined {
  return localizedContent[locale]?.ui;
}

export function localizeServicePage(service: ServicePage, locale: LocaleCode): ServicePage {
  const bundle = localizedContent[locale];
  const alias = SERVICE_CONTENT_ALIASES[service.slug];
  const copy = bundle?.services[service.slug] ?? (alias ? bundle?.services[alias] : undefined);
  if (!copy) return service;
  return {
    ...service,
    ...copy,
    slug: service.slug,
    image: service.image,
    path: service.path,
    category: service.category,
    relatedServiceSlugs: service.relatedServiceSlugs,
    relatedAreaSlugs: service.relatedAreaSlugs,
  };
}

export function localizeServiceArea(area: ServiceArea, locale: LocaleCode): ServiceArea {
  const copy = localizedContent[locale]?.serviceAreas[area.slug];
  const name = localizeAreaName(area.slug, area.name, locale);
  return copy ? { ...area, ...copy, name } : { ...area, name };
}
